import React from "react";
import {
  Box,
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Card, TagChip } from "../../../common";
import {
  colors,
  spacing,
  typography,
  borderRadius,
  shadows,
} from "../../../../theme/tokens";
import { CASE_STUDIES, findCaseStudy } from "./caseStudies";

interface CaseHeadingProps {
  step: string;
  en: string;
  ja: string;
}

function CaseHeading({ step, en, ja }: CaseHeadingProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: spacing[400],
        mb: spacing[800],
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: typography.fontSize["3xl"],
          fontWeight: typography.fontWeight.extrabold,
          color: colors.primary[200],
          lineHeight: typography.lineHeight.none,
        }}
      >
        {step}
      </Typography>
      <Box>
        <Typography
          component="h2"
          sx={{
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.bold,
            color: colors.neutral[900],
          }}
        >
          {ja}
        </Typography>
        <Typography
          sx={{
            mt: spacing[100],
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.semibold,
            letterSpacing: typography.letterSpacing.widest,
            color: colors.primary[600],
          }}
        >
          {en}
        </Typography>
      </Box>
    </Box>
  );
}

interface CaseDetailModalProps {
  slug: string | null;
  onClose: () => void;
  onSelectCase: (slug: string) => void;
}

// 実績詳細モーダル(旧 /case/:slug ページの内容をダイアログ表示)
function CaseDetailModal({
  slug,
  onClose,
  onSelectCase,
}: CaseDetailModalProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const contentRef = React.useRef<HTMLDivElement>(null);
  const caseStudy = findCaseStudy(slug ?? undefined);

  // 「他の事例」切り替え時にモーダル内スクロールを先頭へ戻す
  React.useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [slug]);

  if (!caseStudy) {
    return null;
  }

  const otherCases = CASE_STUDIES.filter(
    (other) => other.slug !== caseStudy.slug
  );
  const titleLines = caseStudy.title.split("\n");

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      aria-labelledby="case-detail-title"
      PaperProps={{
        sx: { borderRadius: fullScreen ? 0 : borderRadius.xl },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="実績詳細を閉じる"
        sx={{
          position: "absolute",
          top: spacing[300],
          right: spacing[300],
          zIndex: 1,
          color: colors.neutral[600],
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          "&:hover": { backgroundColor: colors.neutral[100] },
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent ref={contentRef} sx={{ p: 0 }}>
        <Box
          sx={{
            background: `linear-gradient(180deg, ${colors.primary[50]} 0%, ${colors.neutral[0]} 100%)`,
            pt: { xs: 8, md: 10 },
            pb: { xs: 4, md: 6 },
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: "16px", md: "6vw" } }}>
            <Box sx={{ display: "flex", gap: spacing[200], flexWrap: "wrap" }}>
              <TagChip
                label={caseStudy.category}
                color={
                  caseStudy.category === "受託実績"
                    ? colors.primary[600]
                    : colors.accent.emerald
                }
              />
              {caseStudy.tags.map((tag) => (
                <TagChip key={tag} label={tag} color={colors.neutral[600]} />
              ))}
            </Box>
            <Typography
              id="case-detail-title"
              variant="h1"
              sx={{
                mt: spacing[500],
                fontSize: typography.fontSize["2xl"],
                fontWeight: typography.fontWeight.extrabold,
                lineHeight: typography.lineHeight.snug,
                color: colors.neutral[900],
              }}
            >
              {titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {index > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </Typography>
            <Typography
              sx={{
                mt: spacing[400],
                maxWidth: "720px",
                fontSize: typography.fontSize.base,
                color: colors.neutral[600],
                lineHeight: typography.lineHeight.relaxed,
              }}
            >
              {caseStudy.lead}
            </Typography>
          </Container>
        </Box>

        <Container
          maxWidth="lg"
          sx={{ pb: { xs: 8, md: 12 }, px: { xs: "16px", md: "6vw" } }}
        >
          {/* キービジュアル */}
          <Box
            sx={{
              mt: spacing[400],
              p: { xs: spacing[500], md: spacing[1000] },
              borderRadius: borderRadius.xl,
              background: `linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.neutral[50]} 100%)`,
              border: `1px solid ${colors.neutral[200]}`,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: { xs: spacing[600], md: spacing[800] },
            }}
          >
            <Box
              component="img"
              src={caseStudy.kv.main}
              alt={caseStudy.kv.mainAlt}
              sx={{
                flex: 1,
                minWidth: 0,
                width: "100%",
                borderRadius: borderRadius.md,
                boxShadow: shadows.lg,
                border: `1px solid ${colors.neutral[200]}`,
              }}
            />
            {caseStudy.kv.sub && (
              <Box
                component="img"
                src={caseStudy.kv.sub}
                alt={caseStudy.kv.subAlt}
                sx={{
                  width: { xs: "56%", sm: "40%", md: "200px" },
                  borderRadius: borderRadius.md,
                  boxShadow: shadows.lg,
                  border: `1px solid ${colors.neutral[200]}`,
                }}
              />
            )}
          </Box>

          {/* 数値ハイライト */}
          {caseStudy.stats && (
            <Grid container spacing={3} sx={{ mt: { xs: 2, md: 4 } }}>
              {caseStudy.stats.map((stat) => (
                <Grid item xs={12} sm={4} key={stat.label}>
                  <Box
                    sx={{
                      textAlign: "center",
                      py: spacing[600],
                      borderRadius: borderRadius.lg,
                      backgroundColor: colors.neutral[0],
                      border: `1px solid ${colors.neutral[200]}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: typography.fontSize["2xl"],
                        fontWeight: typography.fontWeight.extrabold,
                        color: colors.primary[600],
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        mt: spacing[100],
                        fontSize: typography.fontSize.sm,
                        color: colors.neutral[500],
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* 01 課題 */}
          <Box sx={{ mt: { xs: 10, md: 14 } }}>
            <CaseHeading
              step="01"
              en={caseStudy.sections.problem.en}
              ja={caseStudy.sections.problem.ja}
            />
            <Grid container spacing={3}>
              {caseStudy.problems.map((problem) => (
                <Grid item xs={12} md={4} key={problem.title}>
                  <Box
                    sx={{
                      height: "100%",
                      p: spacing[600],
                      borderRadius: borderRadius.lg,
                      backgroundColor: colors.neutral[50],
                      borderLeft: `3px solid ${colors.primary[600]}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: typography.fontWeight.bold,
                        fontSize: typography.fontSize.base,
                        color: colors.neutral[800],
                      }}
                    >
                      {problem.title}
                    </Typography>
                    <Typography
                      sx={{
                        mt: spacing[300],
                        fontSize: typography.fontSize.sm,
                        color: colors.neutral[600],
                        lineHeight: typography.lineHeight.relaxed,
                      }}
                    >
                      {problem.body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* 02 仕組み */}
          <Box sx={{ mt: { xs: 10, md: 14 } }}>
            <CaseHeading
              step="02"
              en={caseStudy.sections.solution.en}
              ja={caseStudy.sections.solution.ja}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: spacing[1200], md: spacing[1600] },
              }}
            >
              {caseStudy.features.map((feature, index) => (
                <Grid
                  container
                  spacing={{ xs: 3, md: 6 }}
                  alignItems="center"
                  direction={index % 2 === 1 ? "row-reverse" : "row"}
                  key={feature.title}
                >
                  <Grid item xs={12} md={7}>
                    <Box
                      sx={{
                        borderRadius: borderRadius.lg,
                        backgroundColor: colors.neutral[50],
                        border: `1px solid ${colors.neutral[200]}`,
                        p: { xs: spacing[400], md: spacing[600] },
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={feature.image}
                        alt={feature.alt}
                        sx={{
                          width: feature.mobile
                            ? { xs: "56%", sm: "40%", md: "220px" }
                            : "100%",
                          borderRadius: borderRadius.base,
                          boxShadow: shadows.md,
                          border: `1px solid ${colors.neutral[200]}`,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography
                      sx={{
                        fontSize: typography.fontSize.lg,
                        fontWeight: typography.fontWeight.bold,
                        color: colors.neutral[900],
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      sx={{
                        mt: spacing[300],
                        fontSize: typography.fontSize.sm,
                        color: colors.neutral[600],
                        lineHeight: typography.lineHeight.relaxed,
                      }}
                    >
                      {feature.body}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>

          {/* 03 成果 */}
          <Box sx={{ mt: { xs: 10, md: 14 } }}>
            <CaseHeading
              step="03"
              en={caseStudy.sections.outcome.en}
              ja={caseStudy.sections.outcome.ja}
            />
            <Grid container spacing={3}>
              {caseStudy.outcomes.map((outcome) => (
                <Grid item xs={12} md={4} key={outcome.title}>
                  <Card elevation="low" sx={{ height: "100%" }}>
                    <Box sx={{ p: spacing[600] }}>
                      <Typography
                        sx={{
                          fontWeight: typography.fontWeight.bold,
                          fontSize: typography.fontSize.base,
                          color: colors.primary[700],
                        }}
                      >
                        {outcome.title}
                      </Typography>
                      <Typography
                        sx={{
                          mt: spacing[300],
                          fontSize: typography.fontSize.sm,
                          color: colors.neutral[600],
                          lineHeight: typography.lineHeight.relaxed,
                        }}
                      >
                        {outcome.body}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {caseStudy.tech && (
              <Typography
                sx={{
                  mt: spacing[800],
                  fontSize: typography.fontSize.xs,
                  color: colors.neutral[400],
                }}
              >
                技術構成: {caseStudy.tech}
              </Typography>
            )}
            {caseStudy.url && (
              <Box sx={{ mt: spacing[800], textAlign: "center" }}>
                <Box
                  component="a"
                  href={caseStudy.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-block",
                    px: spacing[800],
                    py: spacing[300],
                    borderRadius: borderRadius.full,
                    backgroundColor: colors.primary[600],
                    color: colors.neutral[0],
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.semibold,
                    textDecoration: "none",
                    "&:hover": { backgroundColor: colors.primary[700] },
                  }}
                >
                  {caseStudy.name} のサイトを見る →
                </Box>
              </Box>
            )}
          </Box>
        </Container>

        {/* 他の事例 */}
        <Box sx={{ backgroundColor: colors.neutral[50] }}>
          <Container
            maxWidth="lg"
            sx={{ py: { xs: 8, md: 10 }, px: { xs: "16px", md: "6vw" } }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: spacing[800],
                textAlign: "center",
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              他の事例も見る
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {otherCases.map((other) => (
                <Grid item xs={12} sm={6} md={4} key={other.slug}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={`${other.name}の実績詳細を見る`}
                    onClick={() => onSelectCase(other.slug)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectCase(other.slug);
                      }
                    }}
                    sx={{
                      display: "block",
                      cursor: "pointer",
                      height: "100%",
                    }}
                  >
                    <Card
                      interactive
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          aspectRatio: "16 / 9",
                          overflow: "hidden",
                          backgroundColor: colors.neutral[50],
                          borderBottom: `1px solid ${colors.neutral[200]}`,
                        }}
                      >
                        <img
                          src={other.cover}
                          alt={other.coverAlt}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "top",
                          }}
                        />
                      </Box>
                      <Box sx={{ p: spacing[500] }}>
                        <Chip
                          label={other.category}
                          size="small"
                          sx={{
                            fontSize: "11px",
                            fontWeight: typography.fontWeight.medium,
                            backgroundColor: colors.primary[50],
                            color: colors.primary[700],
                            borderRadius: borderRadius.full,
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            mt: spacing[200],
                            fontWeight: typography.fontWeight.bold,
                            color: colors.neutral[900],
                          }}
                        >
                          {other.name}
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default CaseDetailModal;
